import {
  catalogBySlug,
  computeWeeklyTargets,
  hardSetSessionsByMuscle,
  mapCatalogToCanonical,
  muscleStateFrom,
  weeklyTargetActualStats,
  weeklyLandmarkReadings,
  type AthleteTargets,
  type BodyMapValue,
  type CatalogMuscleSlug,
  type WorkSetSession,
  workSetSessionsByMuscle,
  type Exercise,
  type MuscleLandmarkReading,
  type MuscleSlug,
  type MuscleState,
  type Program,
  type TargetActualStats,
  type TimedSessionResult,
  type VolumeLandmarkZone,
  type WeekProgress,
  type WeeklyTargetRow,
  type IncompleteReason,
} from "@heraion/shared";

import { landmarkZoneFill } from "../primitives/LandmarkBar";
import {
  gapProposalsFor,
  mavFillsFor,
  weekFillsFor,
  zeroSetRowsUnderMev,
  type ProposalScope,
  type RowGapProposal,
  type RowMavFill,
  type RowWeekFill,
} from "./muscleCoverageProposal";
import type { UiTheme } from "../theme";

// PG-1 — the Progress "muscle coverage" view-model. Pure: composes the SHARED
// weekly scorer (computeWeeklyTargets, the same one Home + the in-workout strip
// use) with the SHARED per-muscle 4-week reference (weeklyTargetActualStats),
// then re-sorts the rows for the Progress hero. ⭐ AP-9 (15/09): l'ordine e la
// vernice della figura seguono la BANDA esterna MEV/MAV/MRV, non più il target
// che scrive l'atleta — quello resta come seconda referenza sulla riga
// (§16.4bis). Qui non vive nessun punteggio: solo l'ordine di presentazione di
// Progress e la vernice della bodymap. Muscle-axis only: coverage is about muscles, so exercise-
// axis targets are dropped (they belong to the Strength section, and the bodymap
// figure is muscle-keyed).

// The coverage scores the athlete's current calendar week against the weekly
// band, whatever the Progress period selector says (that drives the trend
// sections instead): the band is PUBLISHED per week, so the comparison has to be
// per week. The 4-week sparkline carries the longer context. High row cap = the
// full per-muscle census (Progress isn't the compact Home glance).
// ⛔ "The band is weekly" is a fact about the BAND, not a ceiling on this card:
// the per-muscle acute/chronic signal is meant to sit BESIDE it, and a partial
// week is meant to say so. 📖 measurement-contract §8bis (2026-09-15).
const CENSUS_MAX_ROWS = 40;

export type CoverageRow = {
  row: WeeklyTargetRow;
  // The per-muscle reference: this week so far + the 4 complete weeks (PG-17).
  stats: TargetActualStats;
  // AP-9 — this muscle's week read against the EXTERNAL MEV/MAV/MRV band. It is
  // what the row now shows as its primary meter (§16.4bis, Antenore 15/09); the
  // athlete's own target survives as a text-only second reference on the row.
  // Always present on a muscle row: the unbanded three (adductors, abductors,
  // obliques) carry a reading with NO band and NO zone, which is the third
  // state, ⛔ never a borrowed number.
  landmark: MuscleLandmarkReading;
  // PG-20: the row's SECOND job: how much recent load is still recent, as a
  // decaying rate that never resets (§8ter). ⛔ Independent of the band: an
  // unbanded muscle has a state like any other, because it needs no norm.
  state: MuscleState;
  // AP-9b: the newest session with a hard set on this muscle; null = never.
  // Read off the same per-muscle history as `state`. Optional so hand-built
  // rows (fixtures) need no bump; absent reads as never.
  lastTrainedMs?: number | null;
  // AP-9: how to close the gap, only on a row under MEV (muscleCoverageProposal.ts).
  proposal: RowGapProposal | null;
  // AP-9c fill: under MAV with no MEV gap. Read by the «today» draft only,
  // never shown on the row. Absent: none.
  mavFill?: RowMavFill;
  // D-b (01/10): under the band THIS week, average high: the «today» draft only.
  weekFill?: RowWeekFill;
};

export type MuscleCoverage = {
  mode: "scored" | "degraded";
  blockName: string | null;
  // Ordinate sulla BANDA: sotto MEV per primi, poi oltre MRV, poi le due zone
  // allenanti. ⛔ Non più sul `TargetZone` dell'atleta — vedi `landmarkRank`.
  rows: CoverageRow[];
  onTargetCount: number;
  targetCount: number;
  // Rows needing attention (no-coverage or under) — drives how many rows show
  // before the "see all" fold. ⛔ Still measured against the ATHLETE's target,
  // deliberately: the fold is about "what do I look at first", and their own
  // goal is the thing they chose. The lead sentence no longer uses it (AP-9).
  attentionCount: number;
  // AP-9 — muscles under the external MEV threshold. This is what the lead
  // sentence says now, because it is what the rows below it show. ⛔ Counts only
  // muscles WITH a published band: the unbanded three cannot be below a
  // threshold that does not exist for them.
  belowMevCount: number;
  // §8ter — muscles OVER the external MRV. Gemello di `belowMevCount`, e per lo
  // stesso motivo: senza di lui la frase in cima alla card diceva «every measured
  // muscle is inside the band» mentre qualcuno era FUORI, sopra. ⛔ Conta solo i
  // muscoli CON banda pubblicata, come il gemello. ⛔ E non è un allarme: il
  // ritorno si appiattisce, non si perde — vedi §8ter.3.
  aboveMrvCount: number;
  // §8bis.1: how far into the week the counts above are. On the athlete's
  // own `weekStartDay`, the coach's view included.
  week: WeekProgress;
  // #116: only uncatalogued exercises this week, so there is no muscle row to
  // flag. rows is empty; the card says "incomplete" instead of "no data".
  incomplete?: boolean;
  incompleteReasons?: IncompleteReason[];
};

/**
 * Build the Progress muscle-coverage view-model, or null when there's nothing
 * to show (mirrors computeWeeklyTargets' null). Scored against the weekly band,
 * on the athlete's own calendar week.
 */
export const buildMuscleCoverage = (
  sessions: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  program: Program | null,
  prescription: Record<string, number> | null,
  nowMs: number,
  // PG-2 — the athlete's week-start day (0=Sun..6=Sat). Omitted → Monday, via
  // the downstream defaults. Both the scorer and the 4-week stats read it so the
  // hero + trend agree on the same calendar-week boundary.
  weekStartDay?: number,
  // ME-3 — the athlete's own weekly targets (AthleteTargets). The target bands
  // in the census come from these now (the program stays for the prescription
  // coverage). Absent/null → no target bands (the census degrades to neutral
  // rows); the program's embedded targets are not read.
  athleteTargets: AthleteTargets | null = null,
  // AP-9: the athlete's place, to filter the gap proposal. Omitted = the whole
  // catalog, which is what a coach sees.
  proposalScope?: ProposalScope,
): MuscleCoverage | null => {
  const data = computeWeeklyTargets(
    sessions,
    catalog,
    program,
    nowMs,
    prescription,
    { maxRows: CENSUS_MAX_ROWS, weekStartDay },
    athleteTargets,
  );
  if (!data) return null;

  // Muscle-axis only — the coverage figure + labels are muscle-keyed.
  const muscleRows = data.rows.filter((r) => r.axis === "muscle");
  if (muscleRows.length === 0) {
    if (!data.incomplete) return null;
    return {
      mode: "degraded",
      blockName: data.blockName,
      rows: [],
      onTargetCount: 0,
      targetCount: 0,
      attentionCount: 0,
      belowMevCount: 0,
      aboveMrvCount: 0,
      week: data.week,
      incomplete: true,
      ...reasonsOf(data),
    };
  }

  // AP-9 — the external band, read off the SAME hard-set map the rows above are
  // scored from (`computeWeeklyTargets` now hands it out instead of keeping it
  // local). ⛔ Not a second `hardSetsByMuscle` pass: a second pass could drift
  // from the one behind the bars, and two metres that disagree are worse than
  // one. A reading exists for every muscle the reference covers, so the lookup
  // below is by catalog slug and simply misses for the unbanded three.
  const byMuscleLandmark = new Map<string, MuscleLandmarkReading>(
    weeklyLandmarkReadings(data.hardSetsByMuscle).map((r) => [r.muscle, r]),
  );

  // ⛔ L'ordine segue la BANDA, non più il target dell'autore. Le righe mostrano
  // la banda (§16.4bis): ordinarle per `TargetZone` metteva in cima un muscolo
  // sotto il SUO target che la banda però dichiara in zona produttiva, cioè una
  // riga verde in testa e una peggiore più in basso. Due riferimenti, un ordine
  // solo: quello che si vede. (Preso dalla REVIEW, 15/09.)
  const landmarkRank = (r: MuscleLandmarkReading): number =>
    r.zone === "below-mev"
      ? 0
      : r.zone === "above-mrv"
        ? 1
        : r.zone === "mev-mav"
          ? 2
          : r.zone === "mav-mrv"
            ? 3
            : 4; // nessuna banda: non è un giudizio, sta in fondo
  // PG-16 — one index for the whole census. `weeklyTargetActualStats` runs once
  // per muscle row (up to CENSUS_MAX_ROWS = 40) and used to rebuild this exact
  // Map, from this exact unchanged catalog, on every single one of them.
  const index = catalogBySlug(catalog);
  // PG-20: one pass per history map (hard sets, work sets), then one state per row.
  const historyByMuscle = hardSetSessionsByMuscle(sessions, catalog, index);
  // #322: rest starts from any counted work set, not only hard ones.
  const workByMuscle = workSetSessionsByMuscle(sessions, catalog, index);
  // AP-9: in scored mode, every banded muscle under MEV this week gets a row,
  // so the lead count and the rows match. Degraded mode lists the busiest
  // muscles, and a zero row would contradict its lead.
  const shownRows =
    data.mode === "scored"
      ? [
          ...muscleRows,
          ...zeroSetRowsUnderMev(
            byMuscleLandmark.values(),
            new Set(muscleRows.map((r) => r.key)),
            data.incompleteSource ?? data.incomplete === true,
          ),
        ]
      : muscleRows;
  const baseRows = shownRows
    .map((row) => ({
      row,
      stats: weeklyTargetActualStats(
        sessions,
        catalog,
        { axis: row.axis, key: row.key, metric: row.metric },
        nowMs,
        weekStartDay,
        index,
      ),
      // ⛔ A targeted muscle NOT trained at all this week is absent from
      // `hardSetsByMuscle` (the map only holds positive credit), so it has no
      // reading — and zero sets is exactly what it did. Falling back to a
      // zero reading is the honest value, and it is also the one the proposal
      // half needs: a muscle never worked is the signal, not a missing row
      // (volumeLandmarks.ts says so about its own output).
      landmark: byMuscleLandmark.get(row.key) ?? {
        muscle: row.key as CatalogMuscleSlug,
        weeklySets: 0,
      },
      state: muscleStateFrom(historyByMuscle.get(row.key) ?? [], nowMs),
      lastTrainedMs: lastTrained(workByMuscle.get(row.key), nowMs),
    }));
  const proposalCtx = {
    sessions,
    catalog,
    index,
    nowMs,
    weekStartDay,
    scope: proposalScope,
  };
  const proposals = gapProposalsFor(baseRows, proposalCtx);
  const fills = mavFillsFor(baseRows, proposalCtx, proposals);
  const weekFills = weekFillsFor(baseRows, proposalCtx, proposals, fills);
  const rows: CoverageRow[] = baseRows
    .map((r) => {
      const mavFill = fills.get(r.landmark.muscle);
      const weekFill = weekFills.get(r.landmark.muscle);
      return {
        ...r,
        proposal: proposals.get(r.landmark.muscle) ?? null,
        ...(mavFill ? { mavFill } : {}),
        ...(weekFill ? { weekFill } : {}),
      };
    })
    .sort((a, b) => {
      const ra = landmarkRank(a.landmark);
      const rb = landmarkRank(b.landmark);
      if (ra !== rb) return ra - rb;
      // Within a zone: biggest shortfall to target first; untargeted busiest
      // first (no goal to fall short of).
      if (a.row.target == null && b.row.target == null) {
        return b.row.actual - a.row.actual;
      }
      const sa = a.row.target != null ? a.row.target - a.row.actual : 0;
      const sb = b.row.target != null ? b.row.target - b.row.actual : 0;
      return sb - sa;
    });

  return {
    mode: data.mode,
    blockName: data.blockName,
    rows,
    onTargetCount: data.onTargetCount,
    targetCount: data.targetCount,
    // Quante righe stanno aperte prima del «see all». Segue la banda come
    // l'ordine sopra: sotto MEV o oltre MRV sono le due che chiedono una lettura.
    attentionCount: rows.filter(
      (r) => r.landmark.zone === "below-mev" || r.landmark.zone === "above-mrv",
    ).length,
    // The band is WEEKLY (the 15/09 rule): counted on the FULL map of this
    // week's readings. In scored mode every banded muscle under MEV has a row
    // (zeroSetRowsUnderMev), so this equals the below-MEV rows; the 4-week
    // average only decides whether a row carries a proposal.
    belowMevCount: [...byMuscleLandmark.values()].filter(
      (r) => r.zone === "below-mev",
    ).length,
    // Same full map, same reason. An above-MRV muscle was trained this week,
    // so it always has a row.
    aboveMrvCount: [...byMuscleLandmark.values()].filter(
      (r) => r.zone === "above-mrv",
    ).length,
    week: data.week,
    ...(data.incomplete ? { incomplete: true, ...reasonsOf(data) } : {}),
  };
};

// The reasons travel only when written (absent = uncatalogued, #116).
const reasonsOf = (d: { incompleteReasons?: IncompleteReason[] }) =>
  d.incompleteReasons ? { incompleteReasons: d.incompleteReasons } : {};

const lastTrained = (
  history: ReadonlyArray<WorkSetSession> | undefined,
  nowMs: number,
): number | null => {
  let last: number | null = null;
  for (const s of history ?? []) {
    if (s.sets <= 0 || s.startedAtMs > nowMs) continue;
    if (last === null || s.startedAtMs > last) last = s.startedAtMs;
  }
  return last;
};

// Worst-zone-wins when two catalog muscles bridge onto the same bodymap region
// (a region reads as "needs attention" if any contributing muscle does).
// ⛔ Four zones, not three: this is the EXTERNAL band's order, and `below-mev`
// outranks `above-mrv` because too little work is the gap the proposal half
// exists to fill, while too much is a ceiling to notice, not a hole.
const LANDMARK_PRIORITY: Record<VolumeLandmarkZone, number> = {
  "below-mev": 4,
  "above-mrv": 3,
  "mev-mav": 2,
  "mav-mrv": 1,
};

// Paint the dual-figure bodymap with the EXTERNAL band's four zones.
//
// ⭐ Antenore's decision, 15/09 (§16.4bis): the figure used to be painted from
// the athlete's OWN weekly target (`TargetZone`, three zones). It now reads the
// same reference as the rows and the legend under it — the published
// MEV/MAV/MRV band. Before this, the legend said green meant MEV–MAV while the
// figure painted green for "inside your own target": two references sharing one
// paint, and the figure is exactly the surface a reduced-vision reader leans on
// INSTEAD of the dense rows. ⚠️ Declared cost: the figure changes meaning for
// anyone who knew it. It goes in the changelog when this ships.
//
// ⛔ A sibling of the old `muscleZoneBodyMapValue`, which this replaces, NOT a
// generalisation of it: the input is a different type (`MuscleLandmarkReading`
// has no `axis`, no `noCoverage`, and its `muscle` is already a catalog slug),
// the colour map is different by contract, and the conflict priority is
// different. Parameterising one function over all three would be an abstraction
// whose only job is to paper over two domains the contract says are distinct.
//
// Muscles with NO published band are left UNPAINTED — the third state. Painting
// them any colour would say something the reference cannot say.
export const landmarkZoneBodyMapValue = (
  readings: ReadonlyArray<MuscleLandmarkReading>,
  theme: UiTheme,
): BodyMapValue => {
  const best: Partial<Record<MuscleSlug, VolumeLandmarkZone>> = {};
  for (const r of readings) {
    if (r.zone == null) continue;
    for (const bm of mapCatalogToCanonical(r.muscle)) {
      const cur = best[bm];
      if (cur == null || LANDMARK_PRIORITY[r.zone] > LANDMARK_PRIORITY[cur]) {
        best[bm] = r.zone;
      }
    }
  }
  const out: BodyMapValue = {};
  for (const [bm, zone] of Object.entries(best)) {
    if (!zone) continue;
    out[bm as MuscleSlug] = { colour: landmarkZoneFill(theme, zone) };
  }
  return out;
};
